import React, { useEffect, useRef } from "react";

/**
 * Tab navigation.
 *  - Mobile (< sm): wraps into a chip/pill grid so ALL tabs are visible at once.
 *  - Desktop (>= sm): sticky underline row.
 */
export default function TabNav({ tabs, active, onChange }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    // Only scroll into view on desktop layout
    if (window.innerWidth >= 640) {
      const el = scrollRef.current?.querySelector(`[data-tab-id="${active}"]`);
      el?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }
  }, [active]);

  return (
    <div
      className="sticky top-[57px] sm:top-[65px] z-30 bg-background/85 backdrop-blur-md -mx-4 sm:mx-0 px-4 sm:px-0 border-b border-border pt-1 pb-2 sm:pt-0 sm:pb-0"
      data-testid="tab-nav-wrapper"
    >
      {/* MOBILE: chip grid (all tabs visible, no horizontal scroll) */}
      <nav
        className="grid grid-cols-3 gap-1.5 sm:hidden"
        data-testid="tab-nav-mobile"
      >
        {tabs.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              data-tab-id={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => onChange(t.id)}
              className={`w-full text-[9.5px] uppercase tracking-[0.08em] font-bold font-mono rounded-md px-1.5 py-2.5 flex items-center justify-center gap-1 border transition-all ${
                isActive
                  ? "bg-saffron text-white border-saffron shadow-[0_2px_0_hsl(var(--saffron)/0.35)]"
                  : "bg-card text-muted-foreground border-border hover:border-saffron/40 hover:text-foreground"
              }`}
            >
              {t.icon}
              <span className="truncate">{t.label}</span>
              {t.count != null ? (
                <span
                  className={`text-[9px] font-mono px-1 py-0.5 rounded-sm ${
                    isActive
                      ? "bg-white/25 text-white"
                      : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {t.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      {/* DESKTOP: underlined tab row */}
      <nav
        ref={scrollRef}
        className="hidden sm:flex items-end gap-x-1 -mb-px overflow-x-auto no-scrollbar"
        data-testid="tab-nav"
      >
        {tabs.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              data-tab-id={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => onChange(t.id)}
              className={`relative px-4 py-3.5 text-xs uppercase tracking-[0.18em] font-bold font-mono transition-colors flex items-center gap-2 whitespace-nowrap shrink-0 ${
                isActive ? "text-saffron" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.icon}
              <span>{t.label}</span>
              {t.count != null ? (
                <span
                  className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${
                    isActive
                      ? "bg-saffron text-white"
                      : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {t.count}
                </span>
              ) : null}
              {isActive ? (
                <span className="absolute left-2 right-2 -bottom-px h-[2px] bg-saffron" />
              ) : null}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
